/** Renders a picture's vignette: painted background + the picture's posed object (+ overlay props), see types.ts. */
import { memo, useMemo, type CSSProperties } from 'react';
import { useInView } from '../../lib/inView';
import { hasArt, isLightDrawing, type Drawing } from '@sketchling/core';
import { WhenDrawingReady } from '../../components/WhenReady';
import { vignetteOverlay } from './overlay';
import { ArtThumb } from '../Thumb';
import { artSig, vignetteThumbKey } from '../thumbKeys';
export { vignetteOverlay };
import { Figure } from '../Figure';
import { useArtStyle } from '../useArtStyle';
import { vignetteFor, vignetteUrl } from './registry';
import type { VignetteSpec } from './types';
import { VIGNETTE_WIDTHS, pickWidth, variantUrl } from '../../lib/responsiveImage';
import './vignette.css';

export const Vignette = memo(function Vignette({ drawing, activityId, className = '' }: { drawing: Drawing; activityId?: string; className?: string }) {
  const styleId = useArtStyle();
  const spec = vignetteFor({ activityId, drawingId: drawing.id });
  const light = isLightDrawing(drawing);
  const def = useMemo(() => (spec && !light ? vignetteOverlay(drawing, spec) : null), [spec, drawing, light]);
  // The painting is set only while the card is near the screen and dropped when it scrolls away (lib/inView.ts).
  const [ref, near, width] = useInView<HTMLDivElement>();
  if (!spec || (!def && !light)) return null;
  // Shelf cards (≈ 150-190 px) take the 384 variant; big lesson vignettes the 768 source. Unknown width → source.
  const bg = variantUrl(vignetteUrl(spec).replace(/\.webp$/, ''), width ? pickWidth(VIGNETTE_WIDTHS, width) : 768, 768);
  return (
    <div ref={ref} className={`vignette ${className}`} data-vignette={spec.id} style={near ? { backgroundImage: `url(${bg})` } : undefined}>
      <span className="vg-light" aria-hidden style={lightStyle(drawing)} />
      <ArtThumb
        thumbKey={vignetteThumbKey(spec.id, drawing.id)}
        sig={def ? artSig(def) : undefined}
        style={styleId}
        className="vg-fig"
        vector={() => (def ? <Figure def={def} styleId={styleId} noGround className="vg-fig" /> : <WhenDrawingReady drawing={drawing}>{() => <VignetteFigure drawing={drawing} spec={spec} />}</WhenDrawingReady>)}
      />
    </div>
  );
});

function VignetteFigure({ drawing, spec }: { drawing: Drawing; spec: VignetteSpec }) {
  const styleId = useArtStyle();
  const def = useMemo(() => vignetteOverlay(drawing, spec), [drawing, spec]);
  return def ? <Figure def={def} styleId={styleId} noGround className="vg-fig" /> : null;
}

/** The painted glow sits where the picture's own light is (sun / moon / window / lamp). */
function lightStyle(d: Drawing): CSSProperties | undefined {
  const l = d.picture?.light;
  if (!l || (l.x < 14 && l.y < 14)) return undefined; // default sun off the corner: keep the CSS glow
  return { ['--lx' as string]: `${Math.round((l.x / 240) * 100)}%`, ['--ly' as string]: `${Math.round((l.y / 240) * 100)}%` } as CSSProperties;
}

/** True when this drawing (a picture) has a vignette; object lessons never do (plain object on paper). */
export const hasVignette = (drawing: Drawing | undefined, activityId?: string) => hasArt(drawing) && !!drawing?.picture && !!vignetteFor({ activityId, drawingId: drawing.id });
